import { Fragment, useEffect, useMemo, useState } from "react";
import { type DateRange as RdpDateRange } from "react-day-picker";
import { DateTime } from "luxon";
import {
  Calendar as CalendarIcon,
  Check,
  ChevronRight,
  Clock,
  X,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { Separator } from "@/components/ui/separator";
import {
  ActionBarPillClear,
  actionBarButtonActiveClassName,
  actionBarButtonClassName,
  actionBarIconClassName,
} from "@/components/ui/action-bar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

// A committed range as UTC instants (what the filter stores).
export interface DateRangeValue {
  from: Date;
  to: Date;
}

export interface DateRangePickerProps {
  value?: DateRangeValue;
  onChange: (range: DateRangeValue | undefined) => void;
  // IANA timezone the calendar + presets are computed in.
  timezone?: string;
  // Accessible name for the trigger.
  label?: string;
  // Visible trigger text when nothing is applied. Defaults to `label`.
  placeholder?: string;
  // Extra classes for the trigger pill.
  className?: string;
  // Override the quick presets. Defaults to Today / Yesterday / Last 7 / Last 30.
  presets?: readonly DateRangePreset[];
  // Fired with the preset key when a quick preset is picked, for a consumer whose
  // API takes a named period rather than two instants. Supplying it means the
  // consumer owns the preset case: `onChange` is then reserved for custom ranges
  // only, so a preset can't be immediately rewritten as a custom range.
  onPresetApply?: (key: DateRangePresetKey) => void;
  // The applied preset, when the consumer tracks periods by name. Makes the
  // trigger read as the preset's label instead of a date summary, and marks the
  // row in the menu.
  activePresetKey?: DateRangePresetKey;
  // Which preset produced the range that just went out through `onChange` —
  // `null` for a range picked by hand. Fires alongside `onChange`, not instead of
  // it, so a consumer can persist the key (a URL param, say) and hand it back as
  // `activePresetKey`. Without that, a reloaded or shared link has only the dates
  // and the menu can only call them Custom.
  onPresetChange?: (key: DateRangePresetKey | null) => void;
  // Cap on a custom range, in days. Apply is blocked beyond it and the limit is
  // shown — mirrors a server-side cap rather than letting the request 422.
  maxRangeDays?: number;
  // Supplying this puts a clear (×) on the trigger once a range is applied.
  // Omit it where the filter is not optional — Spotlight's period always has a
  // value, so an × there would offer a state the page cannot render.
  onClear?: () => void;
  // Note under the preset list, e.g. which timezone periods are evaluated in.
  footerHint?: string;
}

const START_TIME = "00:00:00";
const END_TIME = "23:59:59";

export type DateRangePresetKey =
  | "today"
  | "yesterday"
  | "thisWeek"
  | "monthToDate"
  | "last7"
  | "last30";

export interface DateRangePreset {
  key: DateRangePresetKey;
  label: string;
  // Presets sharing a group render together; a divider separates one group from
  // the next. Rolling windows and to-date periods answer different questions, so
  // the design keeps them apart. Defaults to group 1.
  group?: number;
}

const DEFAULT_PRESETS: readonly DateRangePreset[] = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "last7", label: "Last 7 days" },
  { key: "last30", label: "Last 30 days" },
  // `thisWeek` has always meant start-of-week → now, which is week-to-date; the
  // label follows the design rather than introducing a second key for it.
  { key: "thisWeek", label: "Week to date", group: 2 },
  { key: "monthToDate", label: "Month to date", group: 2 },
];

// ----- timezone-aware conversions -------------------------------------------
const utcToCalDate = (d: Date, tz: string): Date => {
  const t = DateTime.fromJSDate(d, { zone: "utc" }).setZone(tz);
  // A local-midnight Date whose y/m/d matches the tz calendar day, which is what
  // react-day-picker compares against.
  return new Date(t.year, t.month - 1, t.day);
};

const utcToTimeStr = (d: Date, tz: string): string =>
  DateTime.fromJSDate(d, { zone: "utc" }).setZone(tz).toFormat("HH:mm:ss");

const combineToUtc = (calDate: Date, timeStr: string, tz: string): Date => {
  const [h, m, s] = timeStr.split(":").map((n) => Number(n) || 0);
  return DateTime.fromObject(
    {
      year: calDate.getFullYear(),
      month: calDate.getMonth() + 1,
      day: calDate.getDate(),
      hour: h,
      minute: m,
      second: s,
    },
    { zone: tz }
  )
    .toUTC()
    .toJSDate();
};

// Exported so a consumer that stores the preset *key* in a URL can resolve it back
// to the same two instants this control would have produced. Re-deriving `last7`
// by hand is how a page ends up disagreeing with its own filter pill by a day.
export const dateRangePresetRange = (
  key: DateRangePresetKey,
  tz: string
): DateRangeValue => presetRange(key, tz);

const presetRange = (key: DateRangePresetKey, tz: string): DateRangeValue => {
  const now = DateTime.now().setZone(tz);
  switch (key) {
    case "today":
      return {
        from: now.startOf("day").toUTC().toJSDate(),
        to: now.toUTC().toJSDate(),
      };
    case "yesterday": {
      const y = now.minus({ days: 1 });
      return {
        from: y.startOf("day").toUTC().toJSDate(),
        to: y.endOf("day").toUTC().toJSDate(),
      };
    }
    case "thisWeek":
      return {
        from: now.startOf("week").toUTC().toJSDate(),
        to: now.toUTC().toJSDate(),
      };
    case "monthToDate":
      return {
        from: now.startOf("month").toUTC().toJSDate(),
        to: now.toUTC().toJSDate(),
      };
    // Inclusive of today, matching the server's period resolver (today - 6 /
    // today - 29). Subtracting the full 7 or 30 returned a day more than the
    // label promised, and disagreed with the same-named period on Spotlight.
    case "last7":
      return {
        from: now.minus({ days: 6 }).startOf("day").toUTC().toJSDate(),
        to: now.toUTC().toJSDate(),
      };
    case "last30":
      return {
        from: now.minus({ days: 29 }).startOf("day").toUTC().toJSDate(),
        to: now.toUTC().toJSDate(),
      };
  }
};

// What the trigger reads once a range is applied: `8 Aug` for a single day,
// `2 Aug - 8 Aug` for a span. Always the dates, never the preset that produced
// them — "Last 7 days" tells you the rule, the dates tell you the data.
const summarize = (
  value: DateRangeValue | undefined,
  tz: string
): string | null => {
  if (!value) return null;
  const from = DateTime.fromJSDate(value.from, { zone: "utc" }).setZone(tz);
  const to = DateTime.fromJSDate(value.to, { zone: "utc" }).setZone(tz);
  const thisYear = DateTime.now().setZone(tz).year;
  const fmt = (d: DateTime): string =>
    d.year === thisYear ? d.toFormat("d LLL") : d.toFormat("d LLL yyyy");

  if (from.hasSame(to, "day")) return fmt(from);
  return `${fmt(from)} - ${fmt(to)}`;
};

// Date-range filter control: a pill trigger opening a popover with quick presets
// (Today / Yesterday / Last 7 / Last 30 — applied immediately) and a Custom mode
// with a two-month range calendar + start/end time-of-day, committed via Apply.
// All computation is timezone-aware. Reusable across list pages.
export const DateRangePicker = ({
  value,
  onChange,
  timezone = "UTC",
  label = "Date range",
  placeholder,
  className,
  presets = DEFAULT_PRESETS,
  onPresetApply,
  activePresetKey,
  onPresetChange,
  maxRangeDays,
  onClear,
  footerHint,
}: DateRangePickerProps): React.ReactElement => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"presets" | "custom">("presets");
  // Which preset produced the applied value, with the range it produced. Kept so
  // reopening returns to the list with that preset marked — an applied value is
  // not evidence of a custom range. The range is stored too: if `value` changes
  // from elsewhere (URL navigation, a Clear), the preset no longer describes it.
  const [appliedPreset, setAppliedPreset] = useState<{
    key: DateRangePresetKey;
    from: number;
    to: number;
  } | null>(null);
  const [draft, setDraft] = useState<RdpDateRange | undefined>();
  const [startTime, setStartTime] = useState(START_TIME);
  const [endTime, setEndTime] = useState(END_TIME);

  // Only while the applied value is still the one that preset produced.
  const presetForValue =
    value &&
    appliedPreset &&
    appliedPreset.from === value.from.getTime() &&
    appliedPreset.to === value.to.getTime()
      ? appliedPreset.key
      : null;

  // Seed the draft (and mode) from the committed value each time the popover
  // opens, so re-opening reflects what's applied.
  //
  // Keyed on the two instants rather than the `value` object: callers build the
  // range inline, so a fresh reference arrives on every parent render and would
  // re-run this over the custom range the user is part-way through picking.
  const valueFrom: number | undefined = value?.from.getTime();
  const valueTo: number | undefined = value?.to.getTime();
  useEffect(() => {
    if (!open) return;
    if (valueFrom !== undefined && valueTo !== undefined) {
      const from: Date = new Date(valueFrom);
      const to: Date = new Date(valueTo);
      setMode("presets");
      setDraft({
        from: utcToCalDate(from, timezone),
        to: utcToCalDate(to, timezone),
      });
      setStartTime(utcToTimeStr(from, timezone));
      setEndTime(utcToTimeStr(to, timezone));
    } else {
      setMode("presets");
      setDraft(undefined);
      setStartTime(START_TIME);
      setEndTime(END_TIME);
    }
  }, [open, valueFrom, valueTo, timezone]);

  const applyPreset = (key: DateRangePresetKey): void => {
    // Alternatives, not both: a consumer tracking named periods would otherwise
    // receive the preset and then a resolved range that overwrites it.
    if (onPresetApply) {
      onPresetApply(key);
    } else {
      const range = presetRange(key, timezone);
      setAppliedPreset({
        key,
        from: range.from.getTime(),
        to: range.to.getTime(),
      });
      onPresetChange?.(key);
      onChange(range);
    }
    setOpen(false);
  };

  const draftDays =
    draft?.from && draft.to
      ? Math.floor(
          DateTime.fromJSDate(draft.to).diff(
            DateTime.fromJSDate(draft.from),
            "days"
          ).days
        ) + 1
      : null;
  const exceedsCap =
    maxRangeDays !== undefined &&
    draftDays !== null &&
    draftDays > maxRangeDays;

  const applyCustom = (): void => {
    if (!draft?.from || exceedsCap) return;
    const to = draft.to ?? draft.from;
    // A hand-picked range is not a preset any more.
    setAppliedPreset(null);
    onPresetChange?.(null);
    onChange({
      from: combineToUtc(draft.from, startTime, timezone),
      to: combineToUtc(to, endTime, timezone),
    });
    setOpen(false);
  };

  const summary = useMemo(() => summarize(value, timezone), [value, timezone]);
  // A consumer tracking named periods wins; otherwise our own record.
  const markedPresetKey = activePresetKey ?? presetForValue ?? undefined;
  // A range no preset accounts for was picked by hand, so Custom is what is
  // applied — the row says so rather than the calendar having to open to show it.
  const customIsApplied = !!value && !markedPresetKey;
  const activePresetLabel = activePresetKey
    ? (presets.find((preset) => preset.key === activePresetKey)?.label ?? null)
    : null;
  // Dates win over the preset name: a consumer that tracks periods by name and has
  // no concrete range (Spotlight) still falls back to its label.
  const triggerText = summary ?? activePresetLabel ?? placeholder ?? label;
  const isApplied = !!value || !!activePresetLabel;
  const showClear = isApplied && !!onClear;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {/* The box lives on the wrapper so the clear sits beside the trigger: a
          focusable control inside a <button> is interactive content in a button,
          which the HTML content model forbids. */}
      <div
        data-slot="action-bar-button"
        data-active={isApplied || undefined}
        className={cn(
          actionBarButtonClassName,
          isApplied && actionBarButtonActiveClassName,
          className
        )}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={label}
            className={cn(
              "inline-flex cursor-pointer items-center gap-2 bg-transparent outline-none",
              actionBarIconClassName
            )}
          >
            <CalendarIcon />
            <span>{triggerText}</span>
          </button>
        </PopoverTrigger>
        {showClear && (
          <ActionBarPillClear
            onClear={onClear}
            label={`Clear ${label.toLowerCase()}`}
          />
        )}
      </div>
      {/* Two independent floating cards: the preset menu and the custom
          calendar. The popover itself carries no chrome so each panel reads as
          its own model with a gap between them. */}
      <PopoverContent
        align="start"
        className="w-auto border-0 bg-transparent p-0 shadow-none"
      >
        <div className="flex items-start gap-2">
          {/* Preset card — 208px wide, 12px top / 8px side+bottom padding,
              40px rows with 12px inner padding, per the design. */}
          <div className="w-52 shrink-0 rounded-md border bg-popover px-2 pt-3 pb-2 text-popover-foreground shadow-md">
            <p className="mb-2 px-3 text-xs font-medium text-muted-foreground">
              Select date range
            </p>
            {presets.map((preset, index) => {
              const previous = presets[index - 1];
              const startsGroup =
                !!previous && (previous.group ?? 1) !== (preset.group ?? 1);
              return (
                <Fragment key={preset.key}>
                  {startsGroup && <Separator className="my-2" />}
                  <button
                    type="button"
                    aria-pressed={preset.key === markedPresetKey}
                    className={cn(
                      "flex h-10 w-full cursor-pointer items-center justify-between rounded-md px-3 text-left text-sm hover:bg-accent hover:text-accent-foreground",
                      preset.key === markedPresetKey && "font-medium"
                    )}
                    onClick={() => applyPreset(preset.key)}
                  >
                    {preset.label}
                    {preset.key === markedPresetKey && (
                      <Check className="text-info size-4" />
                    )}
                  </button>
                </Fragment>
              );
            })}
            {/* Divider separating the quick presets from Custom, 8px either side. */}
            <Separator className="my-2" />
            <button
              type="button"
              aria-pressed={customIsApplied}
              className={cn(
                "flex h-10 w-full cursor-pointer items-center justify-between rounded-md px-3 text-left text-sm hover:bg-accent hover:text-accent-foreground",
                mode === "custom" && "bg-accent text-accent-foreground",
                customIsApplied && "font-medium"
              )}
              onClick={() => setMode("custom")}
            >
              Custom
              <span className="flex items-center gap-1">
                {customIsApplied && <Check className="text-info size-4" />}
                <ChevronRight className="size-4 text-muted-foreground" />
              </span>
            </button>
            {footerHint ? (
              <p className="text-muted-foreground mt-2 px-3 pb-1 text-xs">
                {footerHint}
              </p>
            ) : null}
          </div>

          {/* Custom calendar — a separate floating card */}
          {mode === "custom" && (
            <div className="rounded-md border bg-popover text-popover-foreground shadow-md">
              {/* Header row: fixed 40px, 16px horizontal padding, centered,
                  divider below (no top/bottom padding). */}
              <div className="flex h-10 items-center justify-between border-b px-4">
                <span className="flex items-center gap-2 text-sm font-medium">
                  <CalendarIcon className="size-4 text-muted-foreground" />
                  Date range
                </span>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Close"
                  onClick={() => setMode("presets")}
                >
                  <X className="size-4" />
                </Button>
              </div>

              <div className="p-4">
                <Calendar
                  mode="range"
                  numberOfMonths={2}
                  selected={draft}
                  onSelect={setDraft}
                  defaultMonth={draft?.from}
                  // Adjacent-month days stay visible so the leading/trailing
                  // week reads as a real week and the range is easy to judge
                  // across a month boundary. No `fixedWeeks` here: padding a
                  // 5-week month to 6 would append a whole extra row of the
                  // next month's dates, which is redundant next to that
                  // month's own panel.
                  fixedWeeks={false}
                  className="p-0"
                />

                {/* 24px between the calendar and the time row, per the design. */}
                <div className="mt-6 grid grid-cols-2 gap-3">
                  <TimeField
                    label="Start time"
                    value={startTime}
                    onChange={setStartTime}
                  />
                  <TimeField
                    label="End time"
                    value={endTime}
                    onChange={setEndTime}
                  />
                </div>
              </div>

              {/* Footer sits outside the padded body so its divider spans the
                  full card width, mirroring the header. */}
              <div className="flex items-center justify-between gap-3 border-t px-4 py-3">
                {exceedsCap ? (
                  <p className="text-destructive text-xs" role="alert">
                    Maximum {maxRangeDays} days
                  </p>
                ) : (
                  <span />
                )}
                <Button
                  onClick={applyCustom}
                  disabled={!draft?.from || exceedsCap}
                >
                  Apply
                </Button>
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

function TimeField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}): React.ReactElement {
  return (
    <div className="relative">
      <span className="sr-only">{label}</span>
      <Clock className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        type="time"
        step={1}
        value={value}
        aria-label={label}
        onChange={(e) => onChange(e.target.value || value)}
        className={cn(
          "h-9 pl-8",
          // Hide the browser's native picker button: its popup is unstylable
          // browser chrome and it duplicates the Clock icon on the left. The
          // field stays fully editable by typing or arrow-stepping a segment.
          "[&::-webkit-calendar-picker-indicator]:hidden",
          // Keep an affordance so it still reads as an input, not a label.
          "cursor-text hover:border-ring"
        )}
      />
    </div>
  );
}
